

























































import {
	Component,
	Inject,
	InjectReactive,
	Prop,
	ProvideReactive,
	Vue,
	Watch,
} from 'vue-property-decorator';

import { FeedTank, Sensor, SimproJobDetails, Site, Transceiver } from '@/api';
import { FeedProduct, FeedProductModals } from '../../../models';
import { FeedTankService } from '../../../services';

import EntityHeader from '@/components/entity-header/EntityHeader.vue';
import ProvisionFeedTankModal from './components/ProvisionFeedTankModal.vue';
import InstallFeedTankModal from './components/InstallFeedTankModal.vue';
import Toast from '@/components/display/toast.vue';
import { isLastEntityOnProduct } from '@/common/EntityUtils';
import {
	StatusChangeReasonsCancel,
	StatusChangeReasonsLastEntity,
	StatusChangeReasonsRemoval,
} from '@/common/Constants';
import { getAlertText, showHelpTexts } from '@/common/HelpTextUtils';
import { SiteService } from '@/services/site-service';

@Component({
	components: {
		EntityHeader,
		ProvisionFeedTankModal,
		InstallFeedTankModal,
	},
	methods: {
		getAlertText,
		showHelpTexts,
	},
})
export default class FeedTankDetail extends Vue {
	@Prop({ required: true, type: String })
	private feedTankId!: string | null;

	@InjectReactive('feedProduct')
	private feedProduct!: FeedProduct;

	@InjectReactive('feedSimproJobs')
	public readonly feedSimproJobs?: SimproJobDetails[] | null;

	@Inject('refreshFeedProduct')
	private refresh!: () => Promise<void>;

	@Watch('feedProduct')
	@Watch('feedTankId')
	public async onDependenciesChanged() {
		await this.fetchSiteTransceivers();
		this.setFeedTank();
	}

	@ProvideReactive('selectedFeedTank')
	public feedTank: FeedTank | null = null;

	public feedTankService: FeedTankService | null = null;
	public loading: boolean = true;
	public tankDetailRoute: string = 'products.feed.feedtanks.tankdetail';

	@ProvideReactive('siteTransceivers')
	public siteTransceivers: Transceiver[] = [];

	private siteService: SiteService = new SiteService();
	private toast: Toast = new Toast();

	public async created(): Promise<void> {
		await this.fetchSiteTransceivers();
		this.setFeedTank();
		this.loading = false;
	}

	private async fetchSiteTransceivers(): Promise<void> {
		if (!this.feedProduct?.siteId) {
			this.siteTransceivers = [];
			return;
		}
		const { site, errorMessage } = await this.siteService.getSiteById<
			Site & { transceivers?: Transceiver[] }
		>(this.feedProduct.siteId, 'transceivers');
		if (errorMessage) {
			this.toast.makeToast(errorMessage, 'Error fetching site transceivers', 'danger');
			this.siteTransceivers = [];
			return;
		}
		this.siteTransceivers = site?.transceivers ?? [];
	}

	private hasSimproRemovalJobReason() {
		return this.feedSimproJobs?.find((simproJob: SimproJobDetails) => {
			return simproJob.jobType === 'remove' && simproJob.reason != null;
		});
	}

	public statusChangeReasonOptions(removal: boolean) {
		return this.hasSimproRemovalJobReason()
			? []
			: (isLastEntityOnProduct(this.feedProduct)
					? StatusChangeReasonsLastEntity
					: removal
					? StatusChangeReasonsRemoval
					: StatusChangeReasonsCancel
			  ).map(val => ({ value: val, text: val }));
	}

	public openProvisioningModal(): void {
		this.$bvModal.show(FeedProductModals.PROVISION);
	}

	public openInstallModal(): void {
		this.$bvModal.show(FeedProductModals.INSTALL_FEED_TANK);
	}

	public async cancelFeedTank(params: { actionReason?: string | null }): Promise<void> {
		if (!this.feedTankService || !this.feedTank) {
			return;
		}
		const cancelResult = await this.feedTankService.cancelFeedTank(
			this.feedTank,
			params.actionReason,
		);
		if (cancelResult?.errorMessage) {
			this.toast.makeToast(cancelResult.errorMessage, 'Error cancelling feed tank', 'danger');
			return;
		}
		await this.onSuccess({
			successMessage: 'Feed tank cancelled',
			modalId: FeedProductModals.CANCEL + '-' + this.feedTankId,
		});
	}

	public async moveFeedTankToSignOff(): Promise<void> {
		if (!this.feedTankService || !this.feedTank) {
			return;
		}
		const moveToSignOffResult = await this.feedTankService.moveFeedTankToSignOff(this.feedTank);
		if (moveToSignOffResult?.errorMessage) {
			this.toast.makeToast(moveToSignOffResult.errorMessage, 'Error', 'danger');
			return;
		}
		await this.onSuccess({
			successMessage: 'Feed tank moved to sign off',
		});
	}

	public async signOff(): Promise<void> {
		if (!this.feedTankService || !this.feedTank) {
			return;
		}
		const signOffResult = await this.feedTankService.signOffFeedTank(this.feedTank);
		if (signOffResult?.errorMessage) {
			this.toast.makeToast(signOffResult.errorMessage, 'Error signing off feed tank', 'danger');
			return;
		}
		await this.onSuccess({ successMessage: 'Feed tank signed off' });
	}

	public async removeFeedTank(params: { actionReason?: string | null }): Promise<void> {
		if (!this.feedTankService || !this.feedTank) {
			return;
		}
		const removalResult = await this.feedTankService.removeFeedTank(
			this.feedTank,
			params.actionReason,
		);
		if (removalResult?.errorMessage) {
			this.toast.makeToast(removalResult.errorMessage, 'Error', 'danger');
		} else {
			await this.onSuccess({ successMessage: 'Feed tank removed' });
		}
	}

	public async provision(feedTank: FeedTank): Promise<void> {
		if (!this.feedTankService) {
			return;
		}
		const provisionResult = await this.feedTankService.provision(feedTank);
		if (provisionResult?.errorMessage) {
			this.toast.makeToast(provisionResult.errorMessage, 'Error provisioning feed tank', 'danger');
		} else {
			await this.onSuccess({
				successMessage: 'Feed tank provisioned',
				modalId: FeedProductModals.PROVISION,
			});
		}
	}

	public async install(feedTank: FeedTank): Promise<void> {
		if (!this.feedTankService) {
			return;
		}
		const installResult = await this.feedTankService.install(feedTank);
		if (installResult?.errorMessage) {
			this.toast.makeToast(installResult.errorMessage, 'Error installing feed tank', 'danger');
		} else {
			await this.onSuccess({
				successMessage: 'Feed tank installed',
				modalId: FeedProductModals.INSTALL_FEED_TANK,
			});
		}
	}

	private async onSuccess(params: { successMessage: string; modalId?: string }): Promise<void> {
		this.toast.makeToast(params.successMessage, 'Success', 'success');
		if (params.modalId) {
			this.$bvModal.hide(params.modalId);
		}
		await this.refresh();
		this.setFeedTank();
	}

	private async onReload(): Promise<void> {
		await this.refresh();
		this.setFeedTank();
	}

	private setFeedTank(): void {
		this.feedTank = this.getFeedTank();
		this.feedTankService = this.feedTank
			? new FeedTankService(this.feedTank, this.siteTransceivers)
			: null;
	}

	private getFeedTank(): FeedTank | null {
		if (!this.feedTankId) {
			return null;
		}
		const tankId: number = parseInt(this.feedTankId, 10);
		return (
			(this.feedProduct.feedtanks.find(
				(feedTank: FeedTank) => feedTank.id === tankId,
			) as FeedTank) ?? null
		);
	}
}
